import React, { useState, useEffect, useMemo } from 'react'
import { normalizeMediaUrl } from '@/utils/media'

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg=='

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackSrc?: string;
  enableCacheBusting?: boolean;
}

const addCacheBusting = (url: string, enableCacheBusting: boolean = true): string => {
  if (!enableCacheBusting || !url) return url;
  if (url.startsWith('data:')) return url; // Don't bust data URLs

  try {
    const isAbsolute = /^https?:\/\//i.test(url);
    if (isAbsolute) {
      const urlObj = new URL(url);
      // Only cache bust if it's our own domain or relative to root
      if (urlObj.hostname === window.location.hostname || urlObj.hostname === 'localhost') {
        urlObj.searchParams.set('t', Date.now().toString());
        return urlObj.toString();
      }
      return url;
    }

    if (url.startsWith('/')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}t=${Date.now()}`;
    }
  } catch (e) {
    // Ignore URL parsing errors
  }
  return url;
};

export function ImageWithFallback(props: ImageWithFallbackProps) {
  const { src, alt, style, className, fallbackSrc, enableCacheBusting = true, ...rest } = props
  const [didError, setDidError] = useState(false)

  // Reset error state when src changes
  useEffect(() => {
    setDidError(false)
  }, [src])

  const handleError = () => {
    setDidError(true)
  }

  // Memoize URLs to avoid redundant calculations and re-renders
  const finalSrc = useMemo(() => {
    const normalized = src ? (normalizeMediaUrl(src) || src) : src
    return normalized ? addCacheBusting(normalized, enableCacheBusting) : normalized
  }, [src, enableCacheBusting])

  const finalFallback = useMemo(() => {
    const normalized = fallbackSrc ? (normalizeMediaUrl(fallbackSrc) || fallbackSrc) : fallbackSrc
    return normalized ? addCacheBusting(normalized, enableCacheBusting) : normalized
  }, [fallbackSrc, enableCacheBusting])

  if (didError) {
    if (finalFallback) {
      return (
        <img
          src={finalFallback}
          alt={alt || 'Image unavailable'}
          className={className}
          style={style}
          {...rest}
          data-original-url={src}
        />
      )
    }
    return (
      <img
        src={ERROR_IMG_SRC}
        alt={alt || 'Image unavailable'}
        className={className}
        style={style}
        {...rest}
      />
    )
  }

  return (
    <img
      src={finalSrc || ERROR_IMG_SRC}
      alt={alt}
      className={className}
      style={style}
      {...rest}
      onError={handleError}
    />
  )
}
